import { Col, Row } from "react-grid-system";
import Container from "../common/Container";
import toolStyles from "../../sharedStyles/Tool.module.css";
import styles from "./OnlineStatements.module.css";
import classNames from "classnames";
import { toolTypeToName } from "../AccountDetails/utils";
import { ToolType } from "../AccountDetails";
import StyledButton from "../common/buttons/StyledButton";
import { useGetStatements } from "../../lib/api/queries";
import { useCallback, useEffect, useRef, useState } from "react";
import Spinner from "../common/Spinner";
import {
  useScriptDetails,
  useStatementsByYear,
} from "./OnlineStatements.hooks";
import OnlineStatementsTables from "./OnlineStatementsTables/OnlineStatementsTables";
import Select from "../common/inputs/Select";
import ConversationScript from "../common/ConversationScript";
import {
  getDisplayError,
  getStatementUrl,
  HTTPError,
} from "../../lib/api/endpoints";
import { Statement } from "../../lib/api/models";
import { reportError } from "../../lib/bugReporting";
import ToolLoadError from "../common/ToolLoadError";

const strings = {
  close: "Close",
  viewBy: "View By:",
  select: "Select",

  a0347ErrorTitle: "A_0347: ",
  a0347Error: "Account does not have any billing statements at this time",
};

type Props = {
  className?: string;
  dwbuid: string;
  toolId: string;
  onRequestClose: () => void;
};

export default function OnlineStatements({
  className,
  dwbuid,
  toolId,
  onRequestClose,
}: Props) {
  const statementWindowsRef = useRef<(Window | null)[]>([]);

  const [selectedYear, setSelectedYear] = useState<string>();

  const [viewByOptions, setViewByOptions] = useState<
    | undefined
    | Array<{
        label: string;
        value: string;
      }>
  >(undefined);

  const {
    data: getStatementsResponse,
    isLoading: isStatementsLoading,
    isError: isStatementsError,
    error: getStatementsError,
    refetch: getStatementsRefetch,
  } = useGetStatements({
    dwbuid: dwbuid,
    year: selectedYear,
  });

  const statementsByYear = useStatementsByYear(
    getStatementsResponse?.statements ?? [],
  );

  const scriptDetails = useScriptDetails(
    getStatementsResponse?.statements ?? [],
  );

  const isStatementsNotFound =
    isStatementsError &&
    getStatementsError &&
    getStatementsError instanceof HTTPError &&
    getStatementsError.status === 404;

  const openStatementWindow = useCallback(async (statement: Statement) => {
    try {
      const url = statement.url
        ? await getStatementUrl(statement.url, statement.statementDate)
        : "";
      statementWindowsRef.current.push(
        window.open(url, "_blank", "popup=true"),
      );
    } catch (err) {
      reportError(
        new Error(
          `Unable to open online statement for date: ${statement.statementDate}`,
          {
            cause: err,
          },
        ),
      );
    }
  }, []);

  const closeStatementWindow = useCallback(() => {
    statementWindowsRef.current.forEach((window: Window | null) => {
      if (window && !window.closed) {
        window.close();
      }
    });
  }, []);

  // If window is unloaded or component is unmounted,
  // close all statement windows (if opened)
  useEffect(() => {
    window.addEventListener("unload", closeStatementWindow);

    return () => {
      closeStatementWindow();
      window.removeEventListener("unload", closeStatementWindow);
    };
  }, [closeStatementWindow]);

  useEffect(() => {
    if (
      getStatementsResponse?.viewByOptions &&
      getStatementsResponse?.viewByOptions.length > 0 &&
      !selectedYear
    ) {
      setViewByOptions(
        getStatementsResponse.viewByOptions.map((o) => ({
          label: o.value,
          value: o.id,
        })),
      );
      setSelectedYear(getStatementsResponse?.viewByOptions[0].id);
    }
  }, [getStatementsResponse, selectedYear]);

  return (
    <div className={classNames(toolStyles.container, className)} id={toolId}>
      <Container>
        <Row
          className={classNames(
            toolStyles.header,
            toolStyles.headerWithDivider,
          )}
        >
          <Col md={2} className={toolStyles.centerCol}>
            <h2 className={toolStyles.title}>
              {toolTypeToName(ToolType.OnlineStatements)}
            </h2>
          </Col>
          <Col md={2} className={toolStyles.centerCol}>
            <Select
              className={classNames({ [styles.viewByHidden]: !viewByOptions })}
              label={strings.viewBy}
              onChange={(year) => setSelectedYear(year)}
              options={viewByOptions ?? []}
              selectedValue={selectedYear ?? ""}
              disabled={
                isStatementsLoading || isStatementsError || !viewByOptions
              }
            />
          </Col>
          <Col md={2} offset={{ md: 6 }} className={toolStyles.centerCol}>
            <StyledButton variant="secondary" onClick={onRequestClose}>
              {strings.close}
            </StyledButton>
          </Col>
        </Row>
        {isStatementsLoading ? (
          <Row>
            <Col>
              <Spinner />
            </Col>
          </Row>
        ) : isStatementsNotFound ? (
          <Row>
            <Col className={styles.error}>
              <span className={styles.errorTitle}>
                {strings.a0347ErrorTitle}
              </span>
              <span>{strings.a0347Error}</span>
            </Col>
          </Row>
        ) : isStatementsError ? (
          <Row>
            <Col>
              <ToolLoadError
                toolName={toolTypeToName(ToolType.OnlineStatements)}
                errorDescription={getDisplayError(getStatementsError)?.message}
                onRetry={getStatementsRefetch}
              />
            </Col>
          </Row>
        ) : (
          <Row>
            <Col md={6}>
              <OnlineStatementsTables
                years={statementsByYear}
                onOpenStatement={openStatementWindow}
              />
            </Col>
            <Col md={6}>
              {scriptDetails ? (
                <ConversationScript
                  scripts={scriptDetails.scripts}
                  scriptName={scriptDetails.name}
                />
              ) : undefined}
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
}
